// ============================================================
// SalesRecordPDF.js
// Production Sales Record PDF
// ============================================================

import React from "react";

import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from "@react-pdf/renderer";

// ============================================================
// HELPERS
// ============================================================

const money = (value) => {
  const parsed = Number(value || 0);

  return `$${parsed.toFixed(2)}`;
};

const number = (value) => {
  return Number(value || 0).toLocaleString();
};

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ============================================================
// NORMALIZE BACKEND RECORD
// ============================================================

const normalizeSale = (sale) => {
  const revenue = Number(sale?.revenue ?? 0);

  const cost = Number(sale?.cost ?? 0);

  return {
    id: sale?.id ?? "-",

    productName:
      sale?.product_name ||
      sale?.productName ||
      sale?.product?.name ||
      "Unknown Product",

    quantityTaken: Number(
      sale?.quantity_taken ??
        sale?.quantityTaken ??
        0
    ),

    unitsSold: Number(
      sale?.units_sold ??
        sale?.unitsSold ??
        0
    ),

    sellingPriceAtSale: Number(
      sale?.selling_price_at_sale ??
        sale?.sale_price_at_sale ??
        sale?.sellingPriceAtSale ??
        0
    ),

    costPriceAtSale: Number(
      sale?.cost_price_at_sale ??
        sale?.purchase_cost_at_sale ??
        sale?.costPriceAtSale ??
        0
    ),

    revenue,

    cost,

    profit: Number(
      sale?.profit ??
        revenue - cost
    ),

    paymentMethod:
      sale?.payment_method ||
      sale?.paymentMethod ||
      "cash",

    purchasedBy:
      sale?.purchased_by ||
      sale?.purchasedBy ||
      "-",

    yearName:
      sale?.year_name ||
      sale?.yearName ||
      "-",

    createdAt:
      sale?.created_at ||
      sale?.createdAt ||
      null,
  };
};

// ============================================================
// STYLES
// ============================================================

const styles = StyleSheet.create({
  page: {
    paddingTop: 28,
    paddingBottom: 40,
    paddingHorizontal: 24,
    fontSize: 9,
    fontFamily: "Helvetica",
  },

  // ==========================================================
  // HEADER
  // ==========================================================

  header: {
    marginBottom: 16,
    borderBottomWidth: 1.5,
    borderBottomColor: "#1b2a41",
    paddingBottom: 10,
  },

  title: {
    fontSize: 21,
    fontWeight: "bold",
    marginBottom: 5,
    color: "#1b2a41",
  },

  subtitle: {
    fontSize: 10,
    color: "#666666",
  },

  // ==========================================================
  // REPORT INFORMATION
  // ==========================================================

  reportInfo: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 14,
  },

  infoBlock: {
    width: "48%",
  },

  infoLabel: {
    fontSize: 8,
    color: "#777777",
    marginBottom: 3,
    fontWeight: "bold",
  },

  infoValue: {
    fontSize: 10,
    fontWeight: "bold",
  },

  // ==========================================================
  // SUMMARY
  // ==========================================================

  summaryContainer: {
    flexDirection: "row",
    marginBottom: 16,
  },

  summaryCard: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#dddddd",
    padding: 8,
    marginRight: 6,
    borderRadius: 3,
  },

  summaryCardLast: {
    marginRight: 0,
  },

  summaryLabel: {
    fontSize: 7.5,
    color: "#666666",
    marginBottom: 4,
    fontWeight: "bold",
  },

  summaryValue: {
    fontSize: 12,
    fontWeight: "bold",
  },

  // ==========================================================
  // TABLE
  // ==========================================================

  table: {
    width: "100%",
  },

  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#1b2a41",
    paddingVertical: 7,
    paddingHorizontal: 3,
  },

  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#dddddd",
    paddingVertical: 6,
    paddingHorizontal: 3,
  },

  tableRowAlt: {
    backgroundColor: "#f7f7f7",
  },

  cell: {
    paddingHorizontal: 3,
  },

  // ==========================================================
  // TABLE COLUMNS
  //
  // REMOVED:
  // - SELL PRICE
  // - COST PRICE
  // - COST
  //
  // INCLUDED:
  // - PRODUCT
  // - DATE
  // - PURCHASED BY
  // - QTY
  // - UNITS
  // - REVENUE
  // - PROFIT
  // - PAY
  // ==========================================================

  colProduct: {
    width: "21%",
  },

  colDate: {
    width: "13%",
  },

  colPurchasedBy: {
    width: "17%",
  },

  colQty: {
    width: "7%",
    textAlign: "right",
  },

  colUnits: {
    width: "8%",
    textAlign: "right",
  },

  colRevenue: {
    width: "13%",
    textAlign: "right",
  },

  colProfit: {
    width: "13%",
    textAlign: "right",
  },

  colPayment: {
    width: "8%",
    textAlign: "center",
  },

  // ==========================================================
  // TABLE TEXT
  // ==========================================================

  headerText: {
    fontSize: 7.5,
    fontWeight: "bold",
    color: "#ffffff",
  },

  bodyText: {
    fontSize: 8,
  },

  productText: {
    fontSize: 8,
    fontWeight: "bold",
  },

  purchasedByText: {
    fontSize: 8,
  },

  // ==========================================================
  // TOTALS
  // ==========================================================

  totalsRow: {
    flexDirection: "row",
    backgroundColor: "#eeeeee",
    borderTopWidth: 1,
    borderTopColor: "#cccccc",
    paddingVertical: 7,
    paddingHorizontal: 3,
  },

  totalsText: {
    fontSize: 8.5,
    fontWeight: "bold",
  },

  totalsRight: {
    textAlign: "right",
  },

  totalsCenter: {
    textAlign: "center",
  },

  // ==========================================================
  // FOOTER
  // ==========================================================

  footer: {
    position: "absolute",
    bottom: 15,
    left: 24,
    right: 24,
    borderTopWidth: 0.5,
    borderTopColor: "#dddddd",
    paddingTop: 5,
    flexDirection: "row",
    justifyContent: "space-between",
  },

  footerText: {
    fontSize: 7,
    color: "#666666",
  },

  empty: {
    textAlign: "center",
    padding: 25,
    color: "#777777",
    fontSize: 10,
  },
});

// ============================================================
// COMPONENT
// ============================================================

const SalesRecordPDF = ({
  sales = [],
  title = "ZULUWHISKEY SHOPING SALES RECORD",
  reportDate = null,
  yearName = null,
  totals: providedTotals = null,
}) => {
  // ==========================================================
  // NORMALIZE
  // ==========================================================

  const normalizedSales = Array.isArray(sales)
    ? sales.map(normalizeSale)
    : [];

  // ==========================================================
  // TOTALS
  // ==========================================================

  const totals =
    providedTotals ||
    normalizedSales.reduce(
      (acc, sale) => {
        acc.quantity += sale.quantityTaken;

        acc.units += sale.unitsSold;

        acc.revenue += sale.revenue;

        acc.cost += sale.cost;

        acc.profit += sale.profit;

        return acc;
      },
      {
        quantity: 0,
        units: 0,
        revenue: 0,
        cost: 0,
        profit: 0,
      }
    );

  // ==========================================================
  // REPORT DATE
  // ==========================================================

  const displayReportDate = reportDate
    ? formatDate(reportDate)
    : "All Records";

  // ==========================================================
  // GENERATED TIME
  // ==========================================================

  const generatedAt = new Date().toISOString();

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <Document>
      <Page
        size="A4"
        orientation="landscape"
        style={styles.page}
      >
        {/* ==================================================
            HEADER
        ================================================== */}

        <View style={styles.header}>
          <Text style={styles.title}>
            ZULUWHISKEY SHOPING SALES RECORD
          </Text>

          <Text style={styles.subtitle}>
            Sales transaction report — historical backend records
          </Text>
        </View>

        {/* ==================================================
            REPORT INFORMATION
        ================================================== */}

        <View style={styles.reportInfo}>
          <View style={styles.infoBlock}>
            <Text style={styles.infoLabel}>
              REPORT DATE
            </Text>

            <Text style={styles.infoValue}>
              {displayReportDate}
            </Text>
          </View>

          <View style={styles.infoBlock}>
            <Text style={styles.infoLabel}>
              FINANCIAL YEAR
            </Text>

            <Text style={styles.infoValue}>
              {yearName || "All Years"}
            </Text>
          </View>
        </View>

        {/* ==================================================
            SUMMARY
        ================================================== */}

        <View style={styles.summaryContainer}>
          {/* TRANSACTIONS */}

          <View style={styles.summaryCard}>
            <Text style={styles.summaryLabel}>
              TRANSACTIONS
            </Text>

            <Text style={styles.summaryValue}>
              {number(normalizedSales.length)}
            </Text>
          </View>

          {/* UNITS */}

          <View style={styles.summaryCard}>
            <Text style={styles.summaryLabel}>
              UNITS SOLD
            </Text>

            <Text style={styles.summaryValue}>
              {number(totals.units)}
            </Text>
          </View>

          {/* REVENUE */}

          <View style={styles.summaryCard}>
            <Text style={styles.summaryLabel}>
              REVENUE
            </Text>

            <Text style={styles.summaryValue}>
              {money(totals.revenue)}
            </Text>
          </View>

          {/* COST OF SALES */}

          <View style={styles.summaryCard}>
            <Text style={styles.summaryLabel}>
              COST OF SALES
            </Text>

            <Text style={styles.summaryValue}>
              {money(totals.cost)}
            </Text>
          </View>

          {/* NET PROFIT */}

          <View
            style={[
              styles.summaryCard,
              styles.summaryCardLast,
            ]}
          >
            <Text style={styles.summaryLabel}>
              NET PROFIT
            </Text>

            <Text style={styles.summaryValue}>
              {money(totals.profit)}
            </Text>
          </View>
        </View>

        {/* ==================================================
            TABLE
        ================================================== */}

        <View style={styles.table}>
          {/* =================================================
              TABLE HEADER
          ================================================= */}

          <View
            style={styles.tableHeader}
            fixed
          >
            {/* PRODUCT */}

            <Text
              style={[
                styles.cell,
                styles.colProduct,
                styles.headerText,
              ]}
            >
              PRODUCT
            </Text>

            {/* DATE */}

            <Text
              style={[
                styles.cell,
                styles.colDate,
                styles.headerText,
              ]}
            >
              DATE
            </Text>

            {/* PURCHASED BY */}

            <Text
              style={[
                styles.cell,
                styles.colPurchasedBy,
                styles.headerText,
              ]}
            >
              SOLD BY
            </Text>

            {/* QTY */}

            <Text
              style={[
                styles.cell,
                styles.colQty,
                styles.headerText,
              ]}
            >
              QTY
            </Text>

            {/* UNITS */}

            <Text
              style={[
                styles.cell,
                styles.colUnits,
                styles.headerText,
              ]}
            >
              UNITS
            </Text>

            {/* REVENUE */}

            <Text
              style={[
                styles.cell,
                styles.colRevenue,
                styles.headerText,
              ]}
            >
              REVENUE
            </Text>

            {/* PROFIT */}

            <Text
              style={[
                styles.cell,
                styles.colProfit,
                styles.headerText,
              ]}
            >
              PROFIT
            </Text>

            {/* PAYMENT */}

            <Text
              style={[
                styles.cell,
                styles.colPayment,
                styles.headerText,
              ]}
            >
              PAY
            </Text>
          </View>

          {/* =================================================
              TABLE BODY
          ================================================= */}

          {normalizedSales.length === 0 ? (
            <Text style={styles.empty}>
              No sales records found.
            </Text>
          ) : (
            normalizedSales.map(
              (sale, index) => (
                <View
                  key={`${sale.id}-${index}`}
                  style={[
                    styles.tableRow,
                    index % 2 === 1 &&
                      styles.tableRowAlt,
                  ]}
                  wrap={false}
                >
                  {/* PRODUCT */}

                  <Text
                    style={[
                      styles.cell,
                      styles.colProduct,
                      styles.productText,
                    ]}
                  >
                    {sale.productName}
                  </Text>

                  {/* DATE */}

                  <Text
                    style={[
                      styles.cell,
                      styles.colDate,
                      styles.bodyText,
                    ]}
                  >
                    {formatDate(
                      sale.createdAt
                    )}
                  </Text>

                  {/* PURCHASED BY */}

                  <Text
                    style={[
                      styles.cell,
                      styles.colPurchasedBy,
                      styles.purchasedByText,
                    ]}
                  >
                    {sale.purchasedBy}
                  </Text>

                  {/* QTY */}

                  <Text
                    style={[
                      styles.cell,
                      styles.colQty,
                      styles.bodyText,
                    ]}
                  >
                    {number(
                      sale.quantityTaken
                    )}
                  </Text>

                  {/* UNITS */}

                  <Text
                    style={[
                      styles.cell,
                      styles.colUnits,
                      styles.bodyText,
                    ]}
                  >
                    {number(
                      sale.unitsSold
                    )}
                  </Text>

                  {/* REVENUE */}

                  <Text
                    style={[
                      styles.cell,
                      styles.colRevenue,
                      styles.bodyText,
                    ]}
                  >
                    {money(
                      sale.revenue
                    )}
                  </Text>

                  {/* PROFIT */}

                  <Text
                    style={[
                      styles.cell,
                      styles.colProfit,
                      styles.bodyText,
                    ]}
                  >
                    {money(
                      sale.profit
                    )}
                  </Text>

                  {/* PAYMENT */}

                  <Text
                    style={[
                      styles.cell,
                      styles.colPayment,
                      styles.bodyText,
                    ]}
                  >
                    {String(
                      sale.paymentMethod ||
                        "cash"
                    )
                      .replace(
                        /_/g,
                        " "
                      )
                      .substring(
                        0,
                        4
                      )
                      .toUpperCase()}
                  </Text>
                </View>
              )
            )
          )}

          {/* =================================================
              TOTALS
          ================================================= */}

          {normalizedSales.length > 0 && (
            <View style={styles.totalsRow}>
              {/* PRODUCT */}

              <Text
                style={[
                  styles.cell,
                  styles.colProduct,
                  styles.totalsText,
                ]}
              >
                TOTALS
              </Text>

              {/* DATE */}

              <Text
                style={[
                  styles.cell,
                  styles.colDate,
                  styles.totalsText,
                ]}
              />

              {/* PURCHASED BY */}

              <Text
                style={[
                  styles.cell,
                  styles.colPurchasedBy,
                  styles.totalsText,
                ]}
              />

              {/* QTY */}

              <Text
                style={[
                  styles.cell,
                  styles.colQty,
                  styles.totalsText,
                  styles.totalsRight,
                ]}
              >
                {number(
                  totals.quantity
                )}
              </Text>

              {/* UNITS */}

              <Text
                style={[
                  styles.cell,
                  styles.colUnits,
                  styles.totalsText,
                  styles.totalsRight,
                ]}
              >
                {number(
                  totals.units
                )}
              </Text>

              {/* REVENUE */}

              <Text
                style={[
                  styles.cell,
                  styles.colRevenue,
                  styles.totalsText,
                  styles.totalsRight,
                ]}
              >
                {money(
                  totals.revenue
                )}
              </Text>

              {/* PROFIT */}

              <Text
                style={[
                  styles.cell,
                  styles.colProfit,
                  styles.totalsText,
                  styles.totalsRight,
                ]}
              >
                {money(
                  totals.profit
                )}
              </Text>

              {/* PAYMENT */}

              <Text
                style={[
                  styles.cell,
                  styles.colPayment,
                  styles.totalsText,
                ]}
              />
            </View>
          )}
        </View>

        {/* ==================================================
            FOOTER
        ================================================== */}

        <View
          style={styles.footer}
          fixed
        >
          <Text style={styles.footerText}>
            Generated from backend sales records
          </Text>

          <Text style={styles.footerText}>
            Generated:{" "}
            {formatDateTime(
              generatedAt
            )}
          </Text>

          <Text
            style={styles.footerText}
            render={({
              pageNumber,
              totalPages,
            }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
};

export default SalesRecordPDF;